
import { AutomationRuleFormData } from '../schemas/automationRuleSchema';

export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: 'candidate_management' | 'communication' | 'follow_up' | 'status_tracking';
  icon: string;
  popularity: number;
  ruleData: AutomationRuleFormData;
}

export const automationTemplates: AutomationTemplate[] = [
  {
    id: 'new-candidate-welcome',
    name: 'New Candidate Welcome Email',
    description: 'Automatically send a welcome email when a new candidate is added',
    category: 'communication',
    icon: '👋',
    popularity: 95,
    ruleData: {
      name: 'New Candidate Welcome Email',
      description: 'Send a welcome email to new candidates to introduce our recruitment process',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {
          toStatus: 'new'
        }
      },
      actions: [{
        type: 'send_email',
        parameters: {
          template: 'candidate-welcome',
          recipientType: 'candidate',
          delayHours: 0
        }
      }],
      priority: 'medium'
    }
  },
  {
    id: 'interview-reminder',
    name: 'Interview Reminder Task',
    description: 'Create a task to prepare for interviews 1 day before scheduled interviews',
    category: 'candidate_management',
    icon: '📅',
    popularity: 88,
    ruleData: {
      name: 'Interview Preparation Reminder',
      description: 'Create a task to prepare interview materials and questions before scheduled interviews',
      enabled: true,
      trigger: {
        type: 'interview_scheduled',
        conditions: {
          reminderDays: 1
        }
      },
      actions: [{
        type: 'create_task',
        parameters: {
          title: 'Prepare for Interview',
          description: 'Review candidate profile and prepare interview questions',
          priority: 'high',
          dueInDays: 0
        }
      }],
      priority: 'high'
    }
  },
  {
    id: 'weekly-follow-up',
    name: 'Weekly Follow-up Sequence',
    description: 'Schedule weekly follow-ups for candidates in the interview process',
    category: 'follow_up',
    icon: '📞',
    popularity: 76,
    ruleData: {
      name: 'Weekly Follow-up Sequence',
      description: 'Automatically schedule weekly follow-ups for candidates in the interview stage',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {
          toStatus: 'interviewing'
        }
      },
      actions: [{
        type: 'schedule_follow_up',
        parameters: {
          delayDays: 7,
          type: 'email',
          notes: 'Check interview progress and next steps'
        }
      }],
      priority: 'medium'
    }
  },
  {
    id: 'offer-sent-tracking',
    name: 'Offer Response Tracking',
    description: 'Create follow-up tasks when job offers are sent to candidates',
    category: 'status_tracking',
    icon: '💼',
    popularity: 82,
    ruleData: {
      name: 'Offer Response Tracking',
      description: 'Track offer responses and create follow-up tasks for pending offers',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {
          toStatus: 'offer_sent'
        }
      },
      actions: [
        {
          type: 'create_task',
          parameters: {
            title: 'Follow up on Job Offer',
            description: 'Check if candidate has reviewed the offer and answer any questions',
            priority: 'high',
            dueInDays: 3
          }
        },
        {
          type: 'schedule_follow_up',
          parameters: {
            delayDays: 7,
            type: 'call',
            notes: 'Call to discuss offer details if no response received'
          }
        }
      ],
      priority: 'high'
    }
  },
  {
    id: 'application-received',
    name: 'Application Acknowledgment',
    description: 'Send automatic acknowledgment emails when applications are received',
    category: 'communication',
    icon: '📧',
    popularity: 91,
    ruleData: {
      name: 'Application Acknowledgment',
      description: 'Send confirmation email to candidates when their application is received',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {
          toStatus: 'applied'
        }
      },
      actions: [{
        type: 'send_email',
        parameters: {
          template: 'application-received',
          recipientType: 'candidate',
          delayHours: 1
        }
      }],
      priority: 'medium'
    }
  },
  {
    id: 'rejection-notification',
    name: 'Candidate Rejection Notification',
    description: 'Send professional rejection emails and update candidate records',
    category: 'communication',
    icon: '📋',
    popularity: 67,
    ruleData: {
      name: 'Candidate Rejection Notification',
      description: 'Send professional rejection email and update candidate status appropriately',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {
          toStatus: 'rejected'
        }
      },
      actions: [
        {
          type: 'send_email',
          parameters: {
            template: 'rejection-letter',
            recipientType: 'candidate',
            delayHours: 2
          }
        },
        {
          type: 'update_status',
          parameters: {
            newStatus: 'closed',
            addNote: 'Rejection notification sent',
            notifyTeam: false
          }
        }
      ],
      priority: 'medium'
    }
  }
];

export const getTemplatesByCategory = (category: string) => {
  return automationTemplates.filter(template => template.category === category);
};

export const getPopularTemplates = (limit: number = 3) => {
  return automationTemplates
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
};
